import ExcelJS from "exceljs";
const groupPayload = (data: any[]) => {
  const grouped: Record<string, Record<string, any[]>> = {};
  data.forEach((item) => {
    const category = item.category || "Other";
    const subCategory = item.sub_category || "Other";
    if (!grouped[category]) {
      grouped[category] = {};
    }
    if (!grouped[category][subCategory]) {
      grouped[category][subCategory] = [];
    }
    grouped[category][subCategory].push(item);
  });
  return grouped;
};
const toNumber = (value: number | string | null | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatMoney = (value: number): string =>
  `$${value.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const calculateMargin = (sellingPrice: number, cost: number) => {
  const amount = Number((sellingPrice - cost).toFixed(2));
  const percentage =
    sellingPrice > 0 ? Number(((amount / sellingPrice) * 100).toFixed(2)) : 0;
  return { amount, percentage };
};
const applyRowBorder = (
  row: ExcelJS.Row,
  lastVisibleColumn: number,
  style: "thin" | "medium" = "thin",
  color = "FFD7D7D7",
): void => {
  for (let column = 1; column <= lastVisibleColumn; column++) {
    row.getCell(column).border = {
      top: { style, color: { argb: color } },
      left: { style, color: { argb: color } },
      bottom: { style, color: { argb: color } },
      right: { style, color: { argb: color } },
    };
  }
};
const applyCellBorder = (
  cell: ExcelJS.Cell,
  style: "thin" | "medium" = "thin",
  color = "FFD7D7D7",
): void => {
  cell.border = {
    top: { style, color: { argb: color } },
    left: { style, color: { argb: color } },
    bottom: { style, color: { argb: color } },
    right: { style, color: { argb: color } },
  };
};
const columnLetter = (columnNumber: number): string => {
  let number = columnNumber;
  let result = "";
  while (number > 0) {
    const remainder = (number - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    number = Math.floor((number - 1) / 26);
  }
  return result;
};
export const exportRequestExcel = async (
  payload: any[],
  showCost: boolean = false,
  showMsrp: boolean = false,
  showDiscount: boolean = false,
  notes?: string,
  discount?: number,
) => {
  if (!payload.length) {
    console.warn("No data available to export.");
    return;
  }
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Asadi Inc";
  workbook.created = new Date();
  /*
   * Discount is calculated from MSRP, so when showDiscount=true
   * the MSRP column must also be available even if showMsrp=false.
   */
  const displayMsrp = showMsrp || showDiscount;
  const showMargin = showCost && displayMsrp;
  const discountPercentage = Math.max(0, Math.min(toNumber(discount), 100));
  let nextVisibleColumnIndex = 6;
  const costColumnIndex = showCost ? nextVisibleColumnIndex++ : null;
  const msrpColumnIndex = displayMsrp ? nextVisibleColumnIndex++ : null;
  const discountedMsrpColumnIndex = showDiscount
    ? nextVisibleColumnIndex++
    : null;
  const marginColumnIndex = showMargin ? nextVisibleColumnIndex++ : null;
  const visibleColumnCount = nextVisibleColumnIndex - 1;
  const lastVisibleColumnLetter = columnLetter(visibleColumnCount);
  let nextHelperColumnIndex = visibleColumnCount + 1;
  const costHelperColumnIndex = showCost ? nextHelperColumnIndex++ : null;
  const msrpHelperColumnIndex = displayMsrp ? nextHelperColumnIndex++ : null;
  const discountedMsrpHelperColumnIndex = showDiscount
    ? nextHelperColumnIndex++
    : null;
  const marginHelperColumnIndex = showMargin ? nextHelperColumnIndex++ : null;
  const discountRateHelperColumnIndex = showDiscount
    ? nextHelperColumnIndex++
    : null;
  const worksheet = workbook.addWorksheet("Invoice", {
    views: [{ showGridLines: false }],
    pageSetup: {
      orientation: visibleColumnCount >= 6 ? "landscape" : "portrait",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    },
  });
  const worksheetColumns: Partial<ExcelJS.Column>[] = [
    { key: "product", width: 42 },
    { key: "packed", width: 14 },
    { key: "unpacked", width: 16 },
    { key: "initial", width: 12 },
    { key: "totalWeight", width: 18 },
  ];
  if (showCost) {
    worksheetColumns.push({ key: "cost", width: 18 });
  }
  if (displayMsrp) {
    worksheetColumns.push({ key: "msrp", width: 18 });
  }
  if (showDiscount) {
    worksheetColumns.push({ key: "discountedMsrp", width: 20 });
  }
  if (showMargin) {
    worksheetColumns.push({ key: "margin", width: 18 });
  }
  if (showCost) {
    worksheetColumns.push({ key: "lineCostValue", width: 2, hidden: true });
  }
  if (displayMsrp) {
    worksheetColumns.push({ key: "lineMsrpValue", width: 2, hidden: true });
  }
  if (showDiscount) {
    worksheetColumns.push({
      key: "lineDiscountedMsrpValue",
      width: 2,
      hidden: true,
    });
  }
  if (showMargin) {
    worksheetColumns.push({ key: "lineMarginValue", width: 2, hidden: true });
  }
  if (showDiscount) {
    worksheetColumns.push({ key: "discountRate", width: 2, hidden: true });
  }
  worksheet.columns = worksheetColumns;
  if (showDiscount && discountRateHelperColumnIndex) {
    const discountRateCell = worksheet.getCell(
      1,
      discountRateHelperColumnIndex,
    );
    discountRateCell.value = discountPercentage / 100;
    discountRateCell.numFmt = "0.00%";
  }
  /* --------------------------------------------------
   * COMPANY HEADER
   * -------------------------------------------------- */
  worksheet.mergeCells("A1:C1");
  worksheet.getCell("A1").value = "Asadi Inc";
  worksheet.getCell("A1").font = {
    bold: true,
    size: 18,
    color: { argb: "FF6B8E23" },
  };
  worksheet.mergeCells("A2:C5");
  worksheet.getCell("A2").value =
    "125 Martin Ross Avenue\n" +
    "North York ON M3J 2L9\n" +
    "Phone: (416) 665-3003\n" +
    "Email: info@kalamataltd.com";
  worksheet.getCell("A2").alignment = {
    vertical: "top",
    wrapText: true,
  };
  worksheet.getCell("A2").font = {
    size: 10,
    color: { argb: "FF555555" },
  };
  /* --------------------------------------------------
   * INVOICE HEADER
   * -------------------------------------------------- */
  const invoiceStartColumn = Math.max(4, visibleColumnCount - 2);
  const invoiceStartLetter = columnLetter(invoiceStartColumn);
  worksheet.mergeCells(`${invoiceStartLetter}1:${lastVisibleColumnLetter}1`);
  worksheet.getCell(`${invoiceStartLetter}1`).value = "INVOICE";
  worksheet.getCell(`${invoiceStartLetter}1`).font = {
    bold: true,
    size: 20,
    color: { argb: "FF6B8E23" },
  };
  worksheet.getCell(`${invoiceStartLetter}1`).alignment = {
    horizontal: "right",
  };
  worksheet.mergeCells(`${invoiceStartLetter}2:${lastVisibleColumnLetter}5`);
  worksheet.getCell(`${invoiceStartLetter}2`).value =
    `Invoice #: ${payload[0]?.invoice_no || "N/A"}\n` +
    `Date: ${new Date().toLocaleDateString()}\n` +
    `Location: ${payload[0]?.store || ""}\n` +
    `Requested By: ${payload[0]?.first_name || ""}`;
  worksheet.getCell(`${invoiceStartLetter}2`).alignment = {
    horizontal: "right",
    vertical: "top",
    wrapText: true,
  };
  worksheet.getCell(`${invoiceStartLetter}2`).font = { size: 10 };
  worksheet.getRow(2).height = 20;
  worksheet.getRow(3).height = 20;
  worksheet.getRow(4).height = 20;
  worksheet.getRow(5).height = 20;
  /* --------------------------------------------------
   * NOTES
   * -------------------------------------------------- */
  let currentRow = 7;
  if (notes) {
    worksheet.mergeCells(
      `A${currentRow}:${lastVisibleColumnLetter}${currentRow}`,
    );
    const notesCell = worksheet.getCell(`A${currentRow}`);
    notesCell.value = `Notes: ${notes}`;
    notesCell.font = {
      italic: true,
      size: 10,
      color: { argb: "FF555555" },
    };
    notesCell.alignment = { wrapText: true };
    currentRow += 2;
  }
  /* --------------------------------------------------
   * TABLE HEADER
   * -------------------------------------------------- */
  const headerRow = worksheet.getRow(currentRow);
  const headers = [
    "Product",
    "Qty Packed",
    "Qty Unpacked",
    "Initial",
    "Total Weight",
  ];
  if (showCost) {
    headers.push("Cost");
  }
  if (displayMsrp) {
    headers.push("MSRP");
  }
  if (showDiscount) {
    headers.push("Discounted MSRP");
  }
  if (showMargin) {
    headers.push("Margin");
  }
  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF6B8E23" },
    };
    cell.alignment = {
      horizontal: index === 0 ? "left" : "right",
      vertical: "middle",
    };
  });
  headerRow.height = 25;
  applyRowBorder(headerRow, visibleColumnCount, "thin", "FFFFFFFF");
  currentRow++;
  /* --------------------------------------------------
   * GROUP DATA
   * -------------------------------------------------- */
  const groupedData = groupPayload(payload);
  const grandTotals: Record<string, number> = {};
  const categoryTotalRows: number[] = [];
  let grandCostAccountingTotal = 0;
  let grandMsrpAccountingTotal = 0;
  let grandDiscountedMsrpAccountingTotal = 0;
  Object.keys(groupedData).forEach((category) => {
    const categoryTotals: Record<string, number> = {};
    let categoryCostAccountingTotal = 0;
    let categoryMsrpAccountingTotal = 0;
    let categoryDiscountedMsrpAccountingTotal = 0;
    worksheet.mergeCells(
      `A${currentRow}:${lastVisibleColumnLetter}${currentRow}`,
    );
    const categoryCell = worksheet.getCell(`A${currentRow}`);
    categoryCell.value = category.toUpperCase();
    categoryCell.font = { bold: true, size: 11 };
    categoryCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE8F0DD" },
    };
    categoryCell.alignment = { vertical: "middle" };
    worksheet.getRow(currentRow).height = 23;
    applyRowBorder(
      worksheet.getRow(currentRow),
      visibleColumnCount,
      "medium",
      "FF9AAA7D",
    );
    currentRow++;
    const categoryStartRow = currentRow;
    Object.keys(groupedData[category]).forEach((subCategory) => {
      worksheet.mergeCells(
        `A${currentRow}:${lastVisibleColumnLetter}${currentRow}`,
      );
      const subCategoryCell = worksheet.getCell(`A${currentRow}`);
      subCategoryCell.value = subCategory;
      subCategoryCell.font = { bold: true, size: 10 };
      subCategoryCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF4F7EF" },
      };
      subCategoryCell.alignment = {
        indent: 1,
        vertical: "middle",
      };
      applyRowBorder(
        worksheet.getRow(currentRow),
        visibleColumnCount,
        "thin",
        "FFD7D7D7",
      );
      currentRow++;
      groupedData[category][subCategory].forEach((item: any) => {
        const row = worksheet.getRow(currentRow);
        const packed = toNumber(item.packed);
        const qtyPacked = toNumber(item.qty_packed);
        const qtyUnpacked = toNumber(item.qty_unpacked);
        const unit =
          String(item.unit ?? "ITEM")
            .trim()
            .toUpperCase() || "ITEM";
        const totalWeight = Number(
          (packed * qtyPacked + qtyUnpacked).toFixed(2),
        );
        categoryTotals[unit] = Number(
          ((categoryTotals[unit] || 0) + totalWeight).toFixed(2),
        );
        grandTotals[unit] = Number(
          ((grandTotals[unit] || 0) + totalWeight).toFixed(2),
        );
        const packCost = toNumber(item.cost);
        const packMsrp = toNumber(item.msrp);
        const costPerUnit = packed > 0 ? packCost / packed : packCost;
        const msrpPerUnit = packed > 0 ? packMsrp / packed : packMsrp;
        /*
         * qty_packed = number of full packs.
         * qty_unpacked = number of individual units.
         * cost / msrp = price for one full pack.
         */
        const lineCost = Number(
          (qtyPacked * packCost + qtyUnpacked * costPerUnit).toFixed(2),
        );
        const lineMsrp = Number(
          (qtyPacked * packMsrp + qtyUnpacked * msrpPerUnit).toFixed(2),
        );
        const lineDiscountedMsrp = showDiscount
          ? Number((lineMsrp * (1 - discountPercentage / 100)).toFixed(2))
          : lineMsrp;
        const lineDiscountAmount = Number(
          (lineMsrp - lineDiscountedMsrp).toFixed(2),
        );
        const lineSellingPrice = showDiscount ? lineDiscountedMsrp : lineMsrp;
        const lineMargin = calculateMargin(lineSellingPrice, lineCost);
        categoryCostAccountingTotal = Number(
          (categoryCostAccountingTotal + lineCost).toFixed(2),
        );
        categoryMsrpAccountingTotal = Number(
          (categoryMsrpAccountingTotal + lineMsrp).toFixed(2),
        );
        categoryDiscountedMsrpAccountingTotal = Number(
          (categoryDiscountedMsrpAccountingTotal + lineDiscountedMsrp).toFixed(
            2,
          ),
        );
        grandCostAccountingTotal = Number(
          (grandCostAccountingTotal + lineCost).toFixed(2),
        );
        grandMsrpAccountingTotal = Number(
          (grandMsrpAccountingTotal + lineMsrp).toFixed(2),
        );
        grandDiscountedMsrpAccountingTotal = Number(
          (grandDiscountedMsrpAccountingTotal + lineDiscountedMsrp).toFixed(2),
        );
        row.getCell(1).value =
          `${item.product ?? ""}` + (packed > 0 ? ` (${packed} ${unit})` : "");
        row.getCell(2).value = qtyPacked;
        row.getCell(3).value =
          item.qty_unpacked !== undefined && item.qty_unpacked !== null
            ? qtyUnpacked
            : "";
        row.getCell(4).value =
          item.initialQty !== undefined && item.initialQty !== null
            ? toNumber(item.initialQty)
            : "";
        row.getCell(5).value = totalWeight;
        row.getCell(5).numFmt =
          unit === "ITEM" ? '0.00 "ITEMS"' : `0.00 "${unit}"`;
        if (showCost && costColumnIndex && costHelperColumnIndex) {
          row.getCell(costColumnIndex).value = {
            richText: [
              {
                text: `${formatMoney(lineCost)}\n`,
                font: {
                  bold: true,
                  size: 10,
                  color: { argb: "FF111111" },
                },
              },
              {
                text: `${formatMoney(packCost)} / pack\n`,
                font: {
                  size: 8,
                  color: { argb: "FF666666" },
                },
              },
              {
                text: `${formatMoney(costPerUnit)} / ${unit}`,
                font: {
                  bold: true,
                  size: 8,
                  color: { argb: "FF6B8E23" },
                },
              },
            ],
          };
          row.getCell(costHelperColumnIndex).value = lineCost;
          row.getCell(costHelperColumnIndex).numFmt = "$#,##0.00";
        }
        if (displayMsrp && msrpColumnIndex && msrpHelperColumnIndex) {
          row.getCell(msrpColumnIndex).value = {
            richText: [
              {
                text: `${formatMoney(lineMsrp)}\n`,
                font: {
                  bold: true,
                  size: 10,
                  color: { argb: "FF111111" },
                },
              },
              {
                text: `${formatMoney(packMsrp)} / pack\n`,
                font: {
                  size: 8,
                  color: { argb: "FF666666" },
                },
              },
              {
                text: `${formatMoney(msrpPerUnit)} / ${unit}`,
                font: {
                  bold: true,
                  size: 8,
                  color: { argb: "FF6B8E23" },
                },
              },
            ],
          };
          row.getCell(msrpHelperColumnIndex).value = lineMsrp;
          row.getCell(msrpHelperColumnIndex).numFmt = "$#,##0.00";
        }
        if (
          showDiscount &&
          discountedMsrpColumnIndex &&
          discountedMsrpHelperColumnIndex
        ) {
          row.getCell(discountedMsrpColumnIndex).value = {
            richText: [
              {
                text: `${formatMoney(lineDiscountedMsrp)}\n`,
                font: {
                  bold: true,
                  size: 10,
                  color: { argb: "FF6B8E23" },
                },
              },
              {
                text: `-${formatMoney(lineDiscountAmount)} (${discountPercentage}%)`,
                font: {
                  bold: true,
                  size: 8,
                  color: { argb: "FFB42318" },
                },
              },
            ],
          };
          row.getCell(discountedMsrpHelperColumnIndex).value =
            lineDiscountedMsrp;
          row.getCell(discountedMsrpHelperColumnIndex).numFmt = "$#,##0.00";
        }
        if (showMargin && marginColumnIndex && marginHelperColumnIndex) {
          const marginColor = lineMargin.amount < 0 ? "FFB42318" : "FF6B8E23";
          row.getCell(marginColumnIndex).value = {
            richText: [
              {
                text: `${formatMoney(lineMargin.amount)}\n`,
                font: { bold: true, size: 10, color: { argb: marginColor } },
              },
              {
                text: `${lineMargin.percentage.toFixed(2)}%`,
                font: { bold: true, size: 8, color: { argb: marginColor } },
              },
            ],
          };
          row.getCell(marginHelperColumnIndex).value = lineMargin.amount;
          row.getCell(marginHelperColumnIndex).numFmt = "$#,##0.00";
        }
        row.getCell(1).alignment = {
          indent: 2,
          vertical: "middle",
        };
        for (let column = 2; column <= visibleColumnCount; column++) {
          row.getCell(column).alignment = {
            horizontal: "right",
            vertical: "middle",
            wrapText:
              column === costColumnIndex ||
              column === msrpColumnIndex ||
              column === discountedMsrpColumnIndex ||
              column === marginColumnIndex,
          };
        }
        applyRowBorder(row, visibleColumnCount, "thin", "FFE0E0E0");
        row.height = showCost || displayMsrp ? 44 : 20;
        currentRow++;
      });
    });
    /* --------------------------------------------------
     * CATEGORY TOTAL
     * -------------------------------------------------- */
    const categoryEndRow = currentRow - 1;
    const categoryTotalRow = currentRow;
    categoryTotalRows.push(categoryTotalRow);
    worksheet.getCell(`A${currentRow}`).value = `Total ${category}`;
    worksheet.getCell(`A${currentRow}`).font = { bold: true };
    worksheet.getCell(`A${currentRow}`).alignment = {
      horizontal: "right",
    };
    worksheet.getCell(`B${currentRow}`).value = {
      formula: `SUM(B${categoryStartRow}:B${categoryEndRow})`,
    };
    const categoryParts = Object.entries(categoryTotals)
      .filter(([, total]) => total > 0)
      .map(([unit, total]) => {
        if (unit === "ITEM") {
          return `${total} ${total === 1 ? "ITEM" : "ITEMS"}`;
        }
        return `${total.toFixed(2)} ${unit}`;
      });
    worksheet.getCell(`E${currentRow}`).value =
      categoryParts.join(" + ") || "0";
    if (showCost && costColumnIndex && costHelperColumnIndex) {
      const costHelperLetter = columnLetter(costHelperColumnIndex);
      worksheet.getCell(currentRow, costColumnIndex).value = {
        formula: `SUM(${costHelperLetter}${categoryStartRow}:${costHelperLetter}${categoryEndRow})`,
      };
      worksheet.getCell(currentRow, costColumnIndex).numFmt = "$#,##0.00";
    }
    if (displayMsrp && msrpColumnIndex && msrpHelperColumnIndex) {
      const msrpHelperLetter = columnLetter(msrpHelperColumnIndex);
      worksheet.getCell(currentRow, msrpColumnIndex).value = {
        formula: `SUM(${msrpHelperLetter}${categoryStartRow}:${msrpHelperLetter}${categoryEndRow})`,
      };
      worksheet.getCell(currentRow, msrpColumnIndex).numFmt = "$#,##0.00";
    }
    if (
      showDiscount &&
      discountedMsrpColumnIndex &&
      discountedMsrpHelperColumnIndex
    ) {
      const discountedMsrpHelperLetter = columnLetter(
        discountedMsrpHelperColumnIndex,
      );
      const categoryDiscountAmount = Number(
        (
          categoryMsrpAccountingTotal - categoryDiscountedMsrpAccountingTotal
        ).toFixed(2),
      );
      worksheet.getCell(currentRow, discountedMsrpColumnIndex).value = {
        richText: [
          {
            text: `${formatMoney(categoryDiscountedMsrpAccountingTotal)}\n`,
            font: { bold: true, color: { argb: "FF6B8E23" } },
          },
          {
            text: `-${formatMoney(categoryDiscountAmount)} (${discountPercentage}%)`,
            font: { bold: true, size: 8, color: { argb: "FFB42318" } },
          },
        ],
      };
      worksheet.getCell(currentRow, discountedMsrpHelperColumnIndex).value = {
        formula: `SUM(${discountedMsrpHelperLetter}${categoryStartRow}:${discountedMsrpHelperLetter}${categoryEndRow})`,
      };
      worksheet.getCell(currentRow, discountedMsrpHelperColumnIndex).numFmt =
        "$#,##0.00";
    }
    if (showMargin && marginColumnIndex && marginHelperColumnIndex) {
      const marginHelperLetter = columnLetter(marginHelperColumnIndex);
      const categorySellingTotal = showDiscount
        ? categoryDiscountedMsrpAccountingTotal
        : categoryMsrpAccountingTotal;
      const categoryMargin = calculateMargin(
        categorySellingTotal,
        categoryCostAccountingTotal,
      );
      const marginColor = categoryMargin.amount < 0 ? "FFB42318" : "FF6B8E23";
      worksheet.getCell(currentRow, marginColumnIndex).value = {
        richText: [
          {
            text: `${formatMoney(categoryMargin.amount)}\n`,
            font: { bold: true, color: { argb: marginColor } },
          },
          {
            text: `${categoryMargin.percentage.toFixed(2)}%`,
            font: { bold: true, size: 8, color: { argb: marginColor } },
          },
        ],
      };
      worksheet.getCell(currentRow, marginHelperColumnIndex).value = {
        formula: `SUM(${marginHelperLetter}${categoryStartRow}:${marginHelperLetter}${categoryEndRow})`,
      };
      worksheet.getCell(currentRow, marginHelperColumnIndex).numFmt =
        "$#,##0.00";
    }
    const categoryStyledColumns = [
      1,
      2,
      5,
      ...(costColumnIndex ? [costColumnIndex] : []),
      ...(msrpColumnIndex ? [msrpColumnIndex] : []),
      ...(discountedMsrpColumnIndex ? [discountedMsrpColumnIndex] : []),
      ...(marginColumnIndex ? [marginColumnIndex] : []),
    ];
    categoryStyledColumns.forEach((column) => {
      const cell = worksheet.getCell(currentRow, column);
      cell.font = {
        bold: true,
        color: {
          argb: column === 1 ? "FF111111" : "FF6B8E23",
        },
      };
      cell.alignment = {
        horizontal: "right",
        vertical: "middle",
      };
    });
    applyRowBorder(
      worksheet.getRow(currentRow),
      visibleColumnCount,
      "medium",
      "FF9AAA7D",
    );
    worksheet.getRow(currentRow).height = showDiscount || showMargin ? 34 : 22;
    currentRow += 2;
  });
  /* --------------------------------------------------
   * GRAND TOTAL
   * -------------------------------------------------- */
  const grandTotalRow = currentRow;
  worksheet.getCell(`A${currentRow}`).value = "GRAND TOTAL";
  worksheet.getCell(`B${currentRow}`).value = {
    formula: `SUM(${categoryTotalRows.map((row) => `B${row}`).join(",")})`,
  };
  const grandTotalParts = Object.entries(grandTotals)
    .filter(([, total]) => total > 0)
    .map(([unit, total]) => {
      if (unit === "ITEM") {
        return `${total} ${total === 1 ? "ITEM" : "ITEMS"}`;
      }
      return `${total.toFixed(2)} ${unit}`;
    });
  worksheet.getCell(`E${currentRow}`).value =
    grandTotalParts.join(" + ") || "0";
  if (showCost && costColumnIndex) {
    worksheet.getCell(currentRow, costColumnIndex).value = {
      formula: `SUM(${categoryTotalRows
        .map((row) => `${columnLetter(costColumnIndex)}${row}`)
        .join(",")})`,
    };
    worksheet.getCell(currentRow, costColumnIndex).numFmt = "$#,##0.00";
  }
  if (displayMsrp && msrpColumnIndex) {
    worksheet.getCell(currentRow, msrpColumnIndex).value = {
      formula: `SUM(${categoryTotalRows
        .map((row) => `${columnLetter(msrpColumnIndex)}${row}`)
        .join(",")})`,
    };
    worksheet.getCell(currentRow, msrpColumnIndex).numFmt = "$#,##0.00";
  }
  if (
    showDiscount &&
    discountedMsrpColumnIndex &&
    discountedMsrpHelperColumnIndex
  ) {
    const grandDiscountAmount = Number(
      (grandMsrpAccountingTotal - grandDiscountedMsrpAccountingTotal).toFixed(
        2,
      ),
    );
    worksheet.getCell(currentRow, discountedMsrpColumnIndex).value = {
      richText: [
        {
          text: `${formatMoney(grandDiscountedMsrpAccountingTotal)}\n`,
          font: { bold: true, size: 12, color: { argb: "FF6B8E23" } },
        },
        {
          text: `-${formatMoney(grandDiscountAmount)} (${discountPercentage}%)`,
          font: { bold: true, size: 9, color: { argb: "FFB42318" } },
        },
      ],
    };
    worksheet.getCell(currentRow, discountedMsrpHelperColumnIndex).value = {
      formula: `SUM(${categoryTotalRows
        .map((row) => `${columnLetter(discountedMsrpHelperColumnIndex)}${row}`)
        .join(",")})`,
    };
    worksheet.getCell(currentRow, discountedMsrpHelperColumnIndex).numFmt =
      "$#,##0.00";
  }
  if (showMargin && marginColumnIndex && marginHelperColumnIndex) {
    const marginHelperLetter = columnLetter(marginHelperColumnIndex);
    const grandSellingTotal = showDiscount
      ? grandDiscountedMsrpAccountingTotal
      : grandMsrpAccountingTotal;
    const grandMargin = calculateMargin(
      grandSellingTotal,
      grandCostAccountingTotal,
    );
    const marginColor = grandMargin.amount < 0 ? "FFB42318" : "FF6B8E23";
    worksheet.getCell(currentRow, marginColumnIndex).value = {
      richText: [
        {
          text: `${formatMoney(grandMargin.amount)}\n`,
          font: { bold: true, size: 12, color: { argb: marginColor } },
        },
        {
          text: `${grandMargin.percentage.toFixed(2)}%`,
          font: { bold: true, size: 9, color: { argb: marginColor } },
        },
      ],
    };
    worksheet.getCell(currentRow, marginHelperColumnIndex).value = {
      formula: `SUM(${categoryTotalRows
        .map((row) => `${marginHelperLetter}${row}`)
        .join(",")})`,
    };
    worksheet.getCell(currentRow, marginHelperColumnIndex).numFmt = "$#,##0.00";
  }
  const grandStyledColumns = [
    1,
    2,
    5,
    ...(costColumnIndex ? [costColumnIndex] : []),
    ...(msrpColumnIndex ? [msrpColumnIndex] : []),
    ...(discountedMsrpColumnIndex ? [discountedMsrpColumnIndex] : []),
    ...(marginColumnIndex ? [marginColumnIndex] : []),
  ];
  grandStyledColumns.forEach((column) => {
    const cell = worksheet.getCell(currentRow, column);
    cell.font = {
      bold: true,
      size: 12,
      color: {
        argb: column === 1 ? "FF111111" : "FF6B8E23",
      },
    };
    cell.alignment = {
      horizontal: "right",
      vertical: "middle",
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE8F0DD" },
    };
  });
  applyRowBorder(
    worksheet.getRow(currentRow),
    visibleColumnCount,
    "medium",
    "FF6B8E23",
  );
  worksheet.getRow(currentRow).height = showDiscount || showMargin ? 38 : 25;
  currentRow += 2;
  /* --------------------------------------------------
   * OPTIONAL DISCOUNT SUMMARY
   * Reconciles to the sum of discounted line items.
   * -------------------------------------------------- */
  if (
    showDiscount &&
    msrpColumnIndex &&
    discountedMsrpColumnIndex &&
    discountedMsrpHelperColumnIndex
  ) {
    const discountLabelColumn = msrpColumnIndex;
    const discountValueColumn = discountedMsrpColumnIndex;
    const msrpValueLetter = columnLetter(msrpColumnIndex);
    const discountedMsrpValueLetter = columnLetter(
      discountedMsrpHelperColumnIndex,
    );
    worksheet.getCell(currentRow, discountLabelColumn).value =
      `Discount (${discountPercentage}%)`;
    worksheet.getCell(currentRow, discountLabelColumn).font = { bold: true };
    worksheet.getCell(currentRow, discountValueColumn).value = {
      formula: `ROUND(${msrpValueLetter}${grandTotalRow}-${discountedMsrpValueLetter}${grandTotalRow},2)`,
    };
    worksheet.getCell(currentRow, discountValueColumn).numFmt = "-$#,##0.00";
    worksheet.getCell(currentRow, discountValueColumn).font = {
      bold: true,
      color: { argb: "FFB42318" },
    };
    worksheet.getCell(currentRow, discountValueColumn).alignment = {
      horizontal: "right",
    };
    applyCellBorder(
      worksheet.getCell(currentRow, discountLabelColumn),
      "thin",
      "FFD7D7D7",
    );
    applyCellBorder(
      worksheet.getCell(currentRow, discountValueColumn),
      "thin",
      "FFD7D7D7",
    );
    currentRow++;
    worksheet.getCell(currentRow, discountLabelColumn).value =
      "DISCOUNTED MSRP TOTAL";
    worksheet.getCell(currentRow, discountLabelColumn).font = { bold: true };
    worksheet.getCell(currentRow, discountLabelColumn).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE8F0DD" },
    };
    worksheet.getCell(currentRow, discountValueColumn).value = {
      formula: `=${discountedMsrpValueLetter}${grandTotalRow}`,
    };
    worksheet.getCell(currentRow, discountValueColumn).numFmt = "$#,##0.00";
    worksheet.getCell(currentRow, discountValueColumn).font = {
      bold: true,
      size: 11,
      color: { argb: "FF6B8E23" },
    };
    worksheet.getCell(currentRow, discountValueColumn).alignment = {
      horizontal: "right",
    };
    worksheet.getCell(currentRow, discountValueColumn).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE8F0DD" },
    };
    applyCellBorder(
      worksheet.getCell(currentRow, discountLabelColumn),
      "medium",
      "FF9AAA7D",
    );
    applyCellBorder(
      worksheet.getCell(currentRow, discountValueColumn),
      "medium",
      "FF9AAA7D",
    );
    currentRow += 2;
  }
  /* --------------------------------------------------
   * SIGNATURES
   * -------------------------------------------------- */
  const leftSignatureColumn = 1;
  const middleSignatureColumn = Math.max(2, Math.ceil(visibleColumnCount / 2));
  const rightSignatureColumn = visibleColumnCount;
  const signatureColumns = [
    { column: leftSignatureColumn, label: "Storage Keeper", align: "left" },
    {
      column: middleSignatureColumn,
      label: "Driver Delivery",
      align: "center",
    },
    { column: rightSignatureColumn, label: "Recipient", align: "right" },
  ] as const;
  signatureColumns.forEach(({ column, label, align }) => {
    const cell = worksheet.getCell(currentRow, column);
    cell.value = label;
    cell.font = { bold: true, size: 10 };
    cell.alignment = { horizontal: align };
  });
  currentRow += 3;
  signatureColumns.forEach(({ column, align }) => {
    const cell = worksheet.getCell(currentRow, column);
    cell.value = "________________________";
    cell.alignment = { horizontal: align };
  });
  /* --------------------------------------------------
   * PRINT SETTINGS
   * -------------------------------------------------- */
  worksheet.pageSetup.printArea = `A1:${lastVisibleColumnLetter}${currentRow}`;
  /* --------------------------------------------------
   * DOWNLOAD EXCEL
   * -------------------------------------------------- */
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${payload[0]?.invoice_no || "invoice"}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
